import _ from 'lodash';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { useGetFormCustomisationByParentTypes } from 'src/hooks/queries/form-configuration/useGetFormCustomisationByParentTypes';

type GetFormCustomisationData = NonNullable<
  ReturnType<typeof useGetFormCustomisationByParentTypes>['data']
>;
type FormSectionConfiguration = Pick<
  GetFormCustomisationData['form_section_configuration'][number],
  'Description' | 'Label' | 'SectionId' | 'fields' | 'ordering'
>;

type Element = {
  key: string;
  value: ReactElement;
};

type FieldOrdering = {
  FieldId: string;
  Position: number;
};

export interface SectionConfig {
  SectionId: string;
  Label: string;
  Description?: string;
  fields: string[];
}

/**
 * Hardcoded default section, synthesised by `CustomisableFieldWrapper` from
 * `<DefaultSection>` markers in the form's JSX. Same shape as the runtime
 * `SectionConfig` used for rendering — kept as a separate name so the
 * precedence branches in `initializeSections` are self-documenting.
 */
export type DefaultSectionConfig = SectionConfig;

interface SectionManagerReturn {
  sections: SectionConfig[];
  draggedFieldId: string | null;
  handleFieldReorderInSection: (
    sectionId: string,
    newFieldOrder: string[]
  ) => void;
  setDraggedFieldId: (fieldId: string | null) => void;
}

/**
 * Manages section state and field grouping for customizable forms.
 *
 * Precedence — highest wins:
 *   1. Customer-defined `formSections` (from `form_section_configuration`).
 *   2. Hardcoded `defaultSections` declared via `<DefaultSection>` in JSX.
 *   3. Single fallback "Details" section containing all fields.
 *
 * Within whichever branch wins, customer `formFieldOrdering` is applied to the
 * fields of each section. Fields present in `elementsByKey` but absent from
 * the chosen section structure (e.g. newly created custom attributes) are
 * appended to the last section so they still render.
 */
export const useSectionManager = (
  elementsByKey: Element[],
  formSections: FormSectionConfiguration[] | null,
  formFieldOrdering?: FieldOrdering[],
  defaultSections?: DefaultSectionConfig[]
): SectionManagerReturn => {
  const [draggedFieldId, setDraggedFieldId] = useState<string | null>(null);

  const orderFieldsByPosition = useCallback(
    (fieldIds: string[]): string[] => {
      if (!formFieldOrdering) {
        return fieldIds;
      }

      return [...fieldIds].sort((a, b) => {
        const aPos = formFieldOrdering.find((f) => f.FieldId === a)?.Position;
        const bPos = formFieldOrdering.find((f) => f.FieldId === b)?.Position;

        if (aPos === undefined) {
          return 1;
        }
        if (bPos === undefined) {
          return -1;
        }

        return aPos - bPos;
      });
    },
    [formFieldOrdering]
  );

  // Custom fields can be created without yet being mapped to a section; park
  // them at the end of the last section so they still render. Applied to both
  // the customer-sections branch and the hardcoded-defaults branch.
  const appendUnmappedFields = useCallback(
    (sections: SectionConfig[], allFieldIds: string[]): SectionConfig[] => {
      if (sections.length === 0) {
        return sections;
      }
      const mappedFieldIds = new Set(sections.flatMap((s) => s.fields));
      const unmappedFieldIds = allFieldIds.filter(
        (fieldId) => !mappedFieldIds.has(fieldId)
      );
      if (unmappedFieldIds.length === 0) {
        return sections;
      }
      const lastIndex = sections.length - 1;

      return sections.map((section, i) =>
        i === lastIndex
          ? { ...section, fields: [...section.fields, ...unmappedFieldIds] }
          : section
      );
    },
    []
  );

  const initializeSections = useCallback((): SectionConfig[] => {
    const allFieldIds = elementsByKey.map((e) => e.key);

    // 1. Customer-defined sections win when present.
    if (formSections && formSections.length > 0) {
      const sortedFormSections = [...formSections].sort(
        (a, b) => (a.ordering?.Position ?? 0) - (b.ordering?.Position ?? 0)
      );

      const sections = sortedFormSections.map((section) => {
        const sectionFieldIds = section.fields?.map((f) => f.FieldId) ?? [];

        return {
          SectionId: section.SectionId,
          Label: section.Label ?? section.SectionId,
          Description: section.Description ?? '',
          fields: orderFieldsByPosition(sectionFieldIds),
        };
      });

      return appendUnmappedFields(sections, allFieldIds);
    }

    // 2. Hardcoded defaults from `<DefaultSection>` markers.
    if (defaultSections && defaultSections.length > 0) {
      const sections = defaultSections.map((section) => ({
        SectionId: section.SectionId,
        Label: section.Label,
        Description: section.Description ?? '',
        fields: orderFieldsByPosition(section.fields),
      }));

      return appendUnmappedFields(sections, allFieldIds);
    }

    // 3. Fallback: single "Details" section.
    return [
      {
        SectionId: 'section_details',
        Label: 'Details',
        Description: '',
        fields: orderFieldsByPosition(allFieldIds),
      },
    ];
  }, [
    elementsByKey,
    formSections,
    defaultSections,
    orderFieldsByPosition,
    appendUnmappedFields,
  ]);

  const [sections, setSections] = useState<SectionConfig[]>(initializeSections);

  useEffect(() => {
    const newSections = initializeSections();
    if (!_.isEqual(newSections, sections)) {
      setSections(newSections);
    }
    // initializeSections closes over elementsByKey, a new array reference on
    // every render, so including it (or the function itself) would re-run this
    // effect every render. elementsByKey.length is the stable signal we care
    // about (field added/removed). sections is read only by the deep-equality
    // guard above, which exists to break the setSections feedback loop — adding
    // it as a dep would defeat that guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSections, elementsByKey.length, formFieldOrdering, defaultSections]);

  const handleFieldReorderInSection = useCallback(
    (sectionId: string, newFieldOrder: string[]) => {
      setSections((prevSections) =>
        prevSections.map((section) =>
          section.SectionId === sectionId
            ? { ...section, fields: newFieldOrder }
            : section
        )
      );
    },
    []
  );

  return {
    sections,
    draggedFieldId,
    handleFieldReorderInSection,
    setDraggedFieldId,
  };
};
